import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	TablePagination,
	IconButton,
	Tooltip,
} from '@mui/material';
import {
	Delete as DeleteIcon,
	Visibility as VisibilityIcon,
	Edit as EditIcon,
	PersonAdd,
} from '@mui/icons-material';
import {
	getAllCharacterTemplates,
	deleteCharacterTemplate,
} from './../../common/services/characterRequests';
import { CharacterTemplateViewer } from './../../common/components';

const useStyles = makeStyles((theme) => ({
	hidePrevIcon: {
		'& button:has([data-testid="KeyboardArrowLeftIcon"])': {
			display: 'none!important',
		},
	},
	actionButton: {
		marginRight: theme.spacing(1),
	},
}));

const AllCharacterTemplates = () => {
	const classes = useStyles();
	const [templates, setTemplates] = useState([]);
	const [templateToView, setTemplateToView] = useState();
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(1);
	const [nextToken, setNextToken] = useState();

	const navigate = useNavigate();

	useEffect(() => {
		const fetchTemplates = async () => {
			try {
				const response = await getAllCharacterTemplates({
					size: rowsPerPage,
					next: nextToken,
				});
				const { Items, NextToken } = JSON.parse(response.body).data.templates;
				setTemplates((prevTemplates) => [...prevTemplates, ...(Items || [])]);
				setNextToken(NextToken || '');
			} catch (error) {
				console.log('Error getting character templates', error);
			}
		};

		fetchTemplates();
	}, [rowsPerPage, page]);

	const handleDeleteTemplate = async (templateId) => {
		try {
			await deleteCharacterTemplate(templateId);
			setTemplates((prevTemplates) =>
				prevTemplates.filter((template) => template.ID !== templateId)
			);
		} catch (error) {
			console.log('Error deleting character template', error);
		}
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setNextToken();
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
		setTemplates([]);
	};

	return (
		<div>
			<CharacterTemplateViewer
				minimalTemplate={templateToView}
				onClose={() => setTemplateToView()}
			/>
			<TableContainer component={Paper}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Name</TableCell>
							<TableCell>Version</TableCell>
							<TableCell>Game System</TableCell>
							<TableCell>Public</TableCell>
							<TableCell>Owner</TableCell>
							<TableCell>Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{templates.map((template) => (
							<TableRow key={template.ID}>
								<TableCell>{template.name}</TableCell>
								<TableCell>{template.version}</TableCell>
								<TableCell>{template.gameSystem}</TableCell>
								<TableCell>{template.isPublic ? 'Yes' : 'No'}</TableCell>
								<TableCell>{template.owner}</TableCell>
								<TableCell>
									<Tooltip title="View">
										<IconButton
											className={classes.actionButton}
											onClick={() => setTemplateToView(template)}>
											<VisibilityIcon />
										</IconButton>
									</Tooltip>
									<Tooltip title="Creat Character">
										<IconButton
											className={classes.actionButton}
											onClick={() =>
												navigate(`/create_character/${template.ID}`)
											}>
											<PersonAdd />
										</IconButton>
									</Tooltip>
									<Tooltip title="Edit">
										<IconButton
											className={classes.actionButton}
											onClick={() =>
												navigate(`/character_template/${template.ID}`)
											}>
											<EditIcon />
										</IconButton>
									</Tooltip>
									<Tooltip title="Delete">
										<IconButton
											className={classes.actionButton}
											onClick={() => handleDeleteTemplate(template.ID)}>
											<DeleteIcon />
										</IconButton>
									</Tooltip>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				rowsPerPageOptions={[5, 10, 25]}
				component="div"
				count={nextToken ? -1 : templates.length}
				rowsPerPage={rowsPerPage}
				page={page}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				className={classes.hidePrevIcon}
			/>
		</div>
	);
};

export default AllCharacterTemplates;
